import type { ButtonVariants } from '@bambi/component-library/src/components/Button/Button';
import { Button } from '@bambi/component-library/src/components/Button/Button';
import {
  ExclamationTriangleIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/24/solid';
import { isString } from 'lodash-es';
import { useMemo } from 'react';

import { BulletList } from '../BulletList';
import { FormErrorMessage } from '../FormErrorMessage';
import { Modal } from 'src/common/primitives/Modal';

type ConfirmModalMode = 'destructive' | 'neutral' | 'success';

export type ConfirmModalSecondaryAction = {
  label: string;
  onClick: () => void;
  variant?: ButtonVariants;
  testId?: string;
};

export type ConfirmModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  onCancel: () => void;
  cancelText?: string;
  cancelTestId?: string;
  onConfirm: () => void;
  confirmText?: string;
  confirmTestId?: string;
  title: string;
  description?: React.ReactNode;
  loading?: boolean;
  error?: string | string[];
  icon?: React.ReactNode;
  testId?: string;
  mode?: ConfirmModalMode;
  children?: React.ReactNode;
  contentClassnames?: string;
  // When provided, a third button is rendered between confirm and cancel and
  // the three buttons stack vertically (confirm, secondary, cancel).
  secondaryAction?: ConfirmModalSecondaryAction;
};

export function ConfirmModal({
  open,
  setOpen,
  onCancel,
  cancelText = 'Cancel',
  cancelTestId = 'cancelBtn',
  onConfirm,
  confirmText = 'Confirm',
  confirmTestId = 'confirmBtn',
  title,
  description,
  loading,
  error,
  icon,
  testId = 'confirm-modal',
  mode = 'destructive',
  children,
  contentClassnames = 'sm:max-w-[25rem]',
  secondaryAction,
}: ConfirmModalProps) {
  const modeConfig = useMemo(() => ConfirmModalModeToConfigMap[mode], [mode]);

  const confirmButton = (
    <Button
      type="submit"
      variant={modeConfig.confirmButtonVariant}
      className={secondaryAction ? undefined : 'sm:col-span-3'}
      disabled={loading}
      dataTestId={confirmTestId}
      onClick={(e) => {
        e.stopPropagation();
        onConfirm();
      }}
    >
      {confirmText}
    </Button>
  );

  const cancelButton = (
    <Button
      variant={secondaryAction ? 'ghost' : undefined}
      className={secondaryAction ? undefined : 'sm:col-span-3'}
      disabled={loading}
      dataTestId={cancelTestId}
      onClick={(e) => {
        e.stopPropagation();
        onCancel();
      }}
    >
      {cancelText}
    </Button>
  );

  const secondaryButton = secondaryAction ? (
    <Button
      type="button"
      variant={secondaryAction.variant ?? 'secondary-outline'}
      disabled={loading}
      dataTestId={secondaryAction.testId}
      onClick={(e) => {
        e.stopPropagation();
        secondaryAction.onClick();
      }}
    >
      {secondaryAction.label}
    </Button>
  ) : null;

  // A dialog whose request is still in flight must stay open to show the
  // error it may come back with, so it is not dismissible while loading.
  return (
    <Modal
      open={open}
      setOpen={setOpen}
      dismissible={!loading}
      contentClassnames={contentClassnames}
      title={title}
    >
      <div className="flex flex-col gap-3">
        <div className="flex justify-center" data-testid={testId}>
          <div
            className={`${modeConfig.iconContainerClasses} flex h-[48px] w-[48px] items-center justify-center rounded-full border-8`}
          >
            {icon || modeConfig.icon}
          </div>
        </div>
        <h4 className="text-center text-lg font-medium text-gray-900">
          {title}
        </h4>
        {description && (
          <p className="text-center text-sm text-gray-500">{description}</p>
        )}

        {error && (
          <div data-testid="confirm-modal-error-container">
            {isString(error) ? (
              <FormErrorMessage>{error}</FormErrorMessage>
            ) : (
              <div
                className="rounded-md border border-warning-300 bg-warning-25 py-4 pr-2 pl-6 text-warning-600"
                role="alert"
              >
                <BulletList items={error} />
              </div>
            )}
          </div>
        )}

        {children}

        {secondaryAction ? (
          <div className="flex flex-col gap-3">
            {confirmButton}
            {secondaryButton}
            {cancelButton}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
            {cancelButton}
            {confirmButton}
          </div>
        )}
      </div>
    </Modal>
  );
}

type ConfirmModalConfig = {
  icon: React.ReactNode;
  iconContainerClasses?: string;
  confirmButtonVariant?: ButtonVariants;
};

const ConfirmModalModeToConfigMap: Record<
  ConfirmModalMode,
  ConfirmModalConfig
> = {
  destructive: {
    icon: <TrashIcon className="w-5" data-testid="confirm-modal-trash-icon" />,
    iconContainerClasses: 'bg-error-100 text-error-700 border-error-50',
    confirmButtonVariant: 'primary-danger',
  },
  neutral: {
    icon: (
      <ExclamationTriangleIcon
        className="w-5"
        data-testid="confirm-modal-neutral-icon"
      />
    ),
    iconContainerClasses: 'bg-amber-100 text-yellow-900 border-warning-50',
    confirmButtonVariant: 'primary',
  },
  success: {
    icon: (
      <CheckIcon className="w-5" data-testid="confirm-modal-success-icon" />
    ),
    iconContainerClasses: 'bg-success-50 text-mint border-success-200',
    confirmButtonVariant: 'primary',
  },
};
